import { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';

import {
  TUTORIAL_ANCHORS,
  TUTORIAL_PAGE_TRANSITION_WAIT_MS,
  TUTORIAL_ROW_TARGET_WAIT_MS,
  TUTORIAL_SEGMENTS,
  TUTORIAL_SEGMENT_LABELS,
  TUTORIAL_TABLE_LOADED_WAIT_MS,
  TUTORIAL_TARGET_WAIT_TIMEOUT_MS,
  dataTourSelector,
  sleep,
} from '@/constants';
import { MappingTotals, TutorialMappingTab, TutorialStep, UnmappedCounts } from '@/types';
import { waitForElement } from '@/utils';

import { useLatest } from './useLatest';

// Steps whose spotlight target exists only when real rows or counts exist. Once the tier-1
// table-loaded marker is present, such a target either exists or never will, so these steps get a
// short targetWaitTimeout: an absent target then fails fast and the step is skipped (see
// TutorialRunner) instead of freezing the tour.
const ROW_DEPENDENT_TARGETS = new Set(
  [
    TUTORIAL_ANCHORS.pfcRowEditBtn,
    TUTORIAL_ANCHORS.mappingSelectAllCheckbox,
    TUTORIAL_ANCHORS.bulkUnmapBtn,
    TUTORIAL_ANCHORS.bulkAssignBtn,
    TUTORIAL_ANCHORS.loansUnmappedCounter,
    TUTORIAL_ANCHORS.sharesUnmappedCounter,
    TUTORIAL_ANCHORS.pfcUnmappedCounter,
  ].map(dataTourSelector),
);

type Options = {
  isRunning: boolean;
  counts: UnmappedCounts;
  totals: MappingTotals;
  moreActionsTab: TutorialMappingTab | null;
  setMoreActionsTab: (tab: TutorialMappingTab | null) => void;
  bulkSelectTab: TutorialMappingTab | null;
  setBulkSelectTab: (tab: TutorialMappingTab | null) => void;
};

export const useTutorialSteps = ({
  isRunning,
  counts,
  totals,
  moreActionsTab,
  setMoreActionsTab,
  bulkSelectTab,
  setBulkSelectTab,
}: Options): TutorialStep[] => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateRef = useLatest(navigate);
  const pathnameRef = useLatest(location.pathname);
  const moreActionsTabRef = useLatest(moreActionsTab);
  const setMoreActionsTabRef = useLatest(setMoreActionsTab);
  const bulkSelectTabRef = useLatest(bulkSelectTab);
  const setBulkSelectTabRef = useLatest(setBulkSelectTab);

  return useMemo<TutorialStep[]>(() => {
    if (!isRunning) return [];

    const hasCompletedAiresRow = !!document.querySelector(dataTourSelector(TUTORIAL_ANCHORS.dataQualityCell));
    const buildCtx = { counts, totals, hasCompletedAiresRow };

    return TUTORIAL_SEGMENTS.flatMap((segment) => {
      const rawSteps = segment.buildSteps(buildCtx);
      const segmentLabel = TUTORIAL_SEGMENT_LABELS[segment.id];

      return rawSteps.map<TutorialStep>((step, index) => ({
        ...step,
        blockTargetInteraction: true,
        targetWaitTimeout:
          typeof step.target === 'string' && ROW_DEPENDENT_TARGETS.has(step.target)
            ? TUTORIAL_ROW_TARGET_WAIT_MS
            : TUTORIAL_TARGET_WAIT_TIMEOUT_MS,
        data: {
          ...step.data,
          segmentLabel,
          segmentStepIndex: index,
          segmentStepTotal: rawSteps.length,
        },
        before: async () => {
          const data = step.data ?? {};
          const targetRoute = data.segmentRoute;
          const isRouteChange = !!targetRoute && pathnameRef.current !== targetRoute;

          if (isRouteChange) {
            navigateRef.current(targetRoute);
          }

          // Tier 1: wait for the current tab's table to finish loading before a data-dependent step
          // shows, so no step is drawn over the loading skeleton. Runs on every transition. On a
          // route change the previous page is still mounted at this point, so this resolves against
          // its marker immediately (harmless: a segment's first step targets a nav anchor that needs
          // no data); it becomes effective for the same-route steps that follow, once the new page's
          // table has rendered.
          await waitForElement(dataTourSelector(TUTORIAL_ANCHORS.tableLoaded), {
            timeout: TUTORIAL_TABLE_LOADED_WAIT_MS,
          });

          const desiredBulkSelect = data.desiredBulkSelectTab ?? null;
          if (bulkSelectTabRef.current !== desiredBulkSelect) {
            setBulkSelectTabRef.current(desiredBulkSelect);
            if (desiredBulkSelect) {
              await waitForElement(dataTourSelector(TUTORIAL_ANCHORS.bulkUnmapBtn), {
                timeout: TUTORIAL_TARGET_WAIT_TIMEOUT_MS,
              });
            }
          }

          const desired = data.desiredMenuTab ?? null;
          if (moreActionsTabRef.current !== desired) {
            setMoreActionsTabRef.current(desired as TutorialMappingTab | null);
            if (desired) {
              await waitForElement(dataTourSelector(TUTORIAL_ANCHORS.actionDownloadTemplate), {
                timeout: TUTORIAL_TARGET_WAIT_TIMEOUT_MS,
              });
            }
          }

          // Once any menu or bulk UI is open, let the page slide-in transition settle before Joyride
          // measures the target. This runs after the blocks above so a menu-item target already exists;
          // otherwise waiting for it here would block for the full timeout until the menu opens.
          if (isRouteChange && typeof step.target === 'string') {
            const target = await waitForElement(step.target, { timeout: TUTORIAL_TARGET_WAIT_TIMEOUT_MS });
            if (target) {
              await sleep(TUTORIAL_PAGE_TRANSITION_WAIT_MS);
            }
          }
        },
      }));
    });
  }, [
    isRunning,
    counts,
    totals,
    navigateRef,
    pathnameRef,
    moreActionsTabRef,
    setMoreActionsTabRef,
    bulkSelectTabRef,
    setBulkSelectTabRef,
  ]);
};
